import { Users, ShieldQuestion } from 'lucide-react';
import { useFilters } from '@/context/FilterContext';
import { usePost } from '@/lib/useData';
import { PageHeader } from '@/components/common/PageHeader';
import { SectionCard } from '@/components/common/SectionCard';
import { CardSkeleton } from '@/components/common/Loading';
import { EmptyState } from '@/components/common/EmptyState';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import { fmtInt, fmtPct, fmtEur, fmtNum } from '@/lib/format';
import { CampaignCodeTag } from '@/components/campaign/CampaignCodeTag';

export default function Responsables() {
  const { body, clearAll } = useFilters();
  const { data, isLoading } = usePost('/analytics/breakdown', { ...body, dim: 'responsable' });
  const items = (data?.items || []).filter((i) => i.key && i.key !== 'Non renseigné');
  const maxBs = Math.max(1, ...items.map((i) => i.bs_reel));

  return (
    <div className="space-y-6">
      <PageHeader title="Responsables" icon={Users} subtitle="Vue consolidée des campagnes pilotées" />

      <div className="flex items-start gap-2 rounded-2xl border border-dashed bg-muted/30 px-4 py-3 text-sm text-muted-foreground">
        <ShieldQuestion className="mt-0.5 h-4 w-4 shrink-0 accent-text" />
        <span>Conformément à notre politique de confidentialité, seules les <strong className="text-foreground">performances consolidées par responsable</strong> sont affichées. Les résultats individuels des recruteurs ne sont jamais exposés.</span>
      </div>

      {isLoading ? (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">{Array.from({ length: 4 }).map((_, i) => <CardSkeleton key={i} height="h-28" />)}</div>
      ) : items.length === 0 ? <EmptyState onClear={clearAll} /> : (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          {items.map((r) => {
            const codes = r.codes || [];
            return (
              <SectionCard key={r.key} title={r.key} subtitle={`${codes.length || '—'} code(s) campagne piloté(s)`} testid="responsable-card">
                <div className="flex items-end justify-between">
                  <div>
                    <p className="text-xs text-muted-foreground">BS validés consolidés</p>
                    <p className="font-display text-3xl font-semibold tabular">{fmtInt(r.bs_reel)}</p>
                  </div>
                  <Badge variant="secondary" className="rounded-full">{fmtPct(r.taux_transfo)} transfo</Badge>
                </div>
                <Progress value={(r.bs_reel / maxBs) * 100} className="mt-3 h-1.5" />
                <div className="mt-4 grid grid-cols-2 gap-2 text-xs">
                  <div><p className="text-muted-foreground">Don moyen</p><p className="font-medium tabular">{fmtEur(r.don_moyen)}</p></div>
                  <div><p className="text-muted-foreground">Âge moyen</p><p className="font-medium tabular">{fmtNum(r.age_moyen, 1)} ans</p></div>
                  <div><p className="text-muted-foreground">Heures</p><p className="font-medium tabular">{fmtInt(r.heures)}</p></div>
                  <div><p className="text-muted-foreground">Prod.</p><p className="font-medium tabular">{fmtNum(r.productivite, 2)}</p></div>
                </div>
                {codes.length > 0 && (
                  <div className="mt-3 flex flex-wrap gap-1.5">
                    {codes.map((c) => (
                      <CampaignCodeTag
                        key={c.code}
                        code={c.code}
                        canal={c.canal}
                        region={c.region}
                        label={c.label}
                        testid="responsable-code-tag"
                      />
                    ))}
                  </div>
                )}
              </SectionCard>
            );
          })}
        </div>
      )}
    </div>
  );
}
